import { AppLayout } from "../../../layout/AppLayout";
import "react-multi-carousel/lib/styles.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect, useContext, useRef } from "react";
import { useTriviaQuestions } from "../../../api/trivia/useTriviaQuestions";
import { GameOverModal } from "../../../modules/world/rmc/modals/gameOverModal";
import { TransparentLoader } from "../../../modules/world/rmc/modals/transparentLoader";
import { RmcContext } from "../../../components/context/RmcContext";
import { Trivia } from "../../../components/games/Trivia";
import { useServerSyncTimer } from "../../../hooks/useServerSyncTimer";

function RmcTrivia() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const UrltriviaId = searchParams.get("trivia_id");
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score] = useState(0);
  const hasFetched = useRef(false);

  const {
    handleshowTriviaQuestions,
    questions,
    loading,
    handleSubmitAnswer,
    submitting,
    selectedAnswers,
    setSelectedAnswers,
    showGameOver,
    results,
    brandData,
  } = useTriviaQuestions();
  const { brandId, triviaId, slug } = useContext(RmcContext);

  // Initialize server-sync timer for RMC trivia
  const { timeLeft, pauseTimer, setDuration, startTimer } = useServerSyncTimer(
    60, // Default 60 seconds
    () => {
      // Auto-submit when time runs out
      handleSubmitAnswer(UrltriviaId || triviaId, false);
    },
    false, // Don't auto-start, wait for questions to load
  );

  useEffect(() => {
    if (UrltriviaId && !hasFetched.current) {
      hasFetched.current = true;
      handleshowTriviaQuestions(UrltriviaId).then((duration) => {
        if (duration) {
          setDuration(duration);
          startTimer(duration);
        }
      });
    } else if (!UrltriviaId && !hasFetched.current) {
      hasFetched.current = true;
      handleshowTriviaQuestions(triviaId).then((duration) => {
        if (duration) {
          setDuration(duration);
          startTimer(duration);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [UrltriviaId]);

  useEffect(() => {
    if (selectedAnswers.length > 0) {
      setSelectedAnswer(selectedAnswers[currentQuestion]);
    }
  }, [currentQuestion, selectedAnswers]);

  // REFERSH LOGIC
  useEffect(() => {
    const gameOverWasShowing = sessionStorage.getItem("gameOverActive");

    // If either was true during refresh, kick user out
    if (gameOverWasShowing === "true") {
      sessionStorage.removeItem("gameActive");
      sessionStorage.removeItem("gameOverActive");
      navigate(`/worlds/${slug}/rules?id=${brandId}&trivia_id=${triviaId}`);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track game state
  useEffect(() => {
    if (questions.length > 0 && !showGameOver) {
      sessionStorage.setItem("gameActive", "true");
    } else {
      sessionStorage.removeItem("gameActive");
    }
  }, [questions.length, showGameOver]);

  // Track game over state
  useEffect(() => {
    if (showGameOver) {
      sessionStorage.setItem("gameOverActive", "true");
    } else {
      sessionStorage.removeItem("gameOverActive");
    }
  }, [showGameOver]);

  const handleSubmitWithPause = () => {
    pauseTimer(); // Pause the timer using new hook
    sessionStorage.removeItem("gameActive");
    sessionStorage.removeItem("gameOverActive");

    handleSubmitAnswer(UrltriviaId || triviaId, false);
  };

  const handleAnswerSelect = (selectedChoice) => {
    setSelectedAnswers((prev) => {
      const newAnswers = [...prev];
      newAnswers[currentQuestion] = {
        answer_id: selectedChoice?.id,
        question_id: selectedChoice?.question_id,
      };
      return newAnswers;
    });
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion((prev) => prev - 1);
    }
  };
  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
    }
  };

  const currentQ = questions[currentQuestion];
  const isLastQuestion = currentQuestion === questions.length - 1;

  const loadingLength = new Array(12).fill("_");

  const primaryColor = brandData?.secondary_color ?? "#FFD700";

  return (
    <AppLayout bg={"!bg-[#111522] !min-h-svh"} noNav>
      <Trivia
        loading={loading}
        loadingLength={loadingLength}
        score={score}
        timeLeft={timeLeft}
        currentQuestion={currentQuestion}
        questions={questions}
        currentQ={currentQ}
        handleAnswerSelect={handleAnswerSelect}
        selectedAnswer={selectedAnswer}
        selectedAnswers={selectedAnswers}
        handleBack={handleBack}
        handleSubmitWithPause={handleSubmitWithPause}
        isLastQuestion={isLastQuestion}
        handleNext={handleNext}
        color={primaryColor}
      />
      {submitting && <TransparentLoader color={primaryColor} />}
      {showGameOver && (
        <GameOverModal
          results={results}
          brandId={brandId}
          triviaId={triviaId}
          isArena={false}
        />
      )}
    </AppLayout>
  );
}

export default RmcTrivia;
